import React, { useEffect, useRef,useState } from "react";
import ReactDOM from "react-dom";
import { Calendar,ChevronLeft, ChevronRight } from "lucide-react";

import s from "./CustomCalendar.module.css";

const DATE_MASK = "DD-MM-YYYY";
const DIGIT_INDEXES = [0, 1, 3, 4, 6, 7, 8, 9];
const MASK_LENGTH = DATE_MASK.length;
const SEGMENT_RANGES = [
  { start: 0, end: 2, slot: 0, size: 2 },
  { start: 3, end: 5, slot: 2, size: 2 },
  { start: 6, end: 10, slot: 4, size: 4 },
];

const isDigit = (character) => typeof character === "string" && character >= "0" && character <= "9";

/** Reads the eight digit slots out of a DD-MM-YYYY string; slots still showing a mask letter come back as null. */
const readDigitSlots = (displayValue) =>
  DIGIT_INDEXES.map((index) => {
    const character = (displayValue ?? "")[index];

    return isDigit(character) ? character : null;
  });

/** Renders digit slots back onto the fixed DD-MM-YYYY skeleton so every slot keeps its position. */
const writeDigitSlots = (slots) => {
  const characters = DATE_MASK.split("");
  DIGIT_INDEXES.forEach((index, slot) => {
    if (slots[slot] !== null) {
      characters[index] = slots[slot];
    }
  });

  return characters.join("");
};

const isEmptyMask = (slots) => slots.every((slot) => slot === null);
const isCompleteMask = (slots) => slots.every((slot) => slot !== null);

/** Caret positions land on digit slots only — the dashes are never editable. */
const digitIndexAtOrAfter = (index) => DIGIT_INDEXES.find((digitIndex) => digitIndex >= index) ?? MASK_LENGTH;
const digitIndexBefore = (index) => [...DIGIT_INDEXES].reverse().find((digitIndex) => digitIndex < index) ?? DIGIT_INDEXES[0];
const slotOfDigitIndex = (index) => DIGIT_INDEXES.indexOf(index);

const segmentAtIndex = (index) =>
  SEGMENT_RANGES.find(({ start, end }) => index >= start && index < end) ?? SEGMENT_RANGES[SEGMENT_RANGES.length - 1];

const CustomCalendar = ({
  value = null,
  onChange = () => {},
  placeholder = "Select date",
  disabled = false,
  minDate = null,
  maxDate = null,
  error = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(
    value ? new Date(value) : null
  );
  const [inputValue, setInputValue] = useState("");
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [showFormatHint, setShowFormatHint] = useState(false);
  const [selectionTick, setSelectionTick] = useState(0);
  const [formatHintText, setFormatHintText] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  /** Caret/selection to re-apply after a programmatic value change, so typing never jumps to the end. */
  const pendingSelectionRef = useRef(null);
  /** Mirrors isManualEntry for the value-sync effect, which must not re-run when the flag alone flips. */
  const isEditingRef = useRef(false);

  useEffect(() => {

    if (
      !value ||
      value === "Invalid date" ||
      (typeof value === "string" && !value.trim())
    ) {
      setSelectedDate(null);
      if (!isEditingRef.current) {
        setInputValue("");
      }
      return;
    }

    const newDate = new Date(value);

    if (!isNaN(newDate.getTime())) {
      setSelectedDate(newDate);
      setCurrentMonth(newDate);
      if (!isEditingRef.current) {
        setInputValue(formatDate(newDate));
      }
    } else {

      setSelectedDate(null);
      if (!isEditingRef.current) {
        setInputValue("");
      }
    }
  }, [value]);

  useEffect(() => {
    const pendingSelection = pendingSelectionRef.current;

    if (pendingSelection === null || !inputRef.current) return;

    pendingSelectionRef.current = null;
    inputRef.current.setSelectionRange(pendingSelection.start, pendingSelection.end);
  }, [selectionTick]);

  useEffect(() => {
    if (selectedDate && !isManualEntry) {
      setInputValue(formatDate(selectedDate));
    }
  }, [selectedDate, isManualEntry]);

  const formatDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return "";

    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    if (isNaN(day) || isNaN(month) || isNaN(year)) return "";

    const dayStr = day.toString().padStart(2, "0");
    const monthStr = month.toString().padStart(2, "0");

    return `${dayStr}-${monthStr}-${year}`;
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const isDateDisabled = (date) => {
    // Compare dates by their date portion only (ignore time/timezone)
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (minDate) {
      const min = new Date(minDate);
      const minOnly = new Date(min.getFullYear(), min.getMonth(), min.getDate());
      if (dateOnly < minOnly) return true;
    }

    if (maxDate) {
      const max = new Date(maxDate);
      const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate());
      if (dateOnly > maxOnly) return true;
    }

    return false;
  };

  /**
   * Why a complete, well-formed date was refused — so a blur that puts the old value
   * back is understood rather than looking like the field ate the edit.
   */
  const describeDisabledDate = (date) => {
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (minDate) {
      const min = new Date(minDate);
      const minOnly = new Date(min.getFullYear(), min.getMonth(), min.getDate());

      if (dateOnly < minOnly) {
        return `Date cannot be earlier than ${formatDate(minOnly)}`;
      }
    }

    if (maxDate) {
      const max = new Date(maxDate);
      const maxOnly = new Date(max.getFullYear(), max.getMonth(), max.getDate());
      const isToday = maxOnly.getTime() === new Date(
        new Date().getFullYear(),
        new Date().getMonth(),
        new Date().getDate()
      ).getTime();

      if (dateOnly > maxOnly) {
        return isToday
          ? "Date cannot be in the future"
          : `Date cannot be later than ${formatDate(maxOnly)}`;
      }
    }

    return "That date cannot be used here";
  };

  const handleDateClick = (day) => {
    if (disabled) return;

    const newDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );
    if (isDateDisabled(newDate)) return;

    setSelectedDate(newDate);
    setInputValue(formatDate(newDate));
    onChange(newDate);
    setIsOpen(false);
    setIsManualEntry(false);
    isEditingRef.current = false;
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(newMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const handleMonthChange = (monthIndex) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(monthIndex);
    setCurrentMonth(newMonth);
  };

  const handleYearChange = (year) => {
    const newMonth = new Date(currentMonth);
    newMonth.setFullYear(year);
    setCurrentMonth(newMonth);
  };

  const getYearRange = () => {
    const currentYear = new Date().getFullYear();
    let startYear = 1900;
    let endYear = currentYear + 10;

    if (minDate) {
      const minYear = new Date(minDate).getFullYear();
      startYear = Math.max(startYear, minYear);
    }

    if (maxDate) {
      const maxYear = new Date(maxDate).getFullYear();
      endYear = Math.min(endYear, maxYear);
    }

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return years;
  };

  const parseManualDate = (dateString) => {
    if (!dateString) return null;

    const dashPattern = /^(\d{1,2})-(\d{1,2})-(\d{4})$/;
    const dashMatch = dateString.match(dashPattern);

    if (dashMatch) {
      const day = parseInt(dashMatch[1], 10);
      const month = parseInt(dashMatch[2], 10);
      const year = parseInt(dashMatch[3], 10);

      if (
        year >= 1900 &&
        year <= new Date().getFullYear() + 10 &&
        month >= 1 &&
        month <= 12 &&
        day >= 1 &&
        day <= 31
      ) {

        const date = new Date(year, month - 1, day);

        if (
          date.getFullYear() === year &&
          date.getMonth() === month - 1 &&
          date.getDate() === day &&
          !isNaN(date.getTime())
        ) {
          return date;
        }
      }
    }

    return null;
  };

  const getSegmentBounds = (segment) => {
    if (segment.slot === 0) return { min: 1, max: 31, fallback: 1 };
    if (segment.slot === 2) return { min: 1, max: 12, fallback: 1 };

    const currentYear = new Date().getFullYear();

    return { min: 1900, max: currentYear + 10, fallback: currentYear };
  };

  /** Writes a slot buffer back to the input at a fixed caret, notifying the parent only once the mask is filled. */
  const commitSlots = (slots, selection) => {
    const isEmpty = isEmptyMask(slots);

    setIsManualEntry(true);
    isEditingRef.current = true;
    pendingSelectionRef.current = isEmpty ? { start: 0, end: 0 } : selection;
    setInputValue(isEmpty ? "" : writeDigitSlots(slots));
    setSelectionTick((previous) => previous + 1);

    if (isEmpty) {
      setSelectedDate(null);
      onChange(null);
      return;
    }

    if (!isCompleteMask(slots)) return;

    const parsedDate = parseManualDate(writeDigitSlots(slots));

    if (parsedDate && !isDateDisabled(parsedDate)) {
      setSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
      onChange(parsedDate);
      setFormatHintText("✓ Date accepted");
      return;
    }

    setFormatHintText("Enter date as DD-MM-YYYY");
  };

  const clearSlotsInRange = (slots, start, end) => {
    const nextSlots = [...slots];
    DIGIT_INDEXES.filter((index) => index >= start && index < end).forEach((index) => {
      nextSlots[slotOfDigitIndex(index)] = null;
    });

    return nextSlots;
  };

  /**
   * Single keystrokes are handled here rather than through onChange, so a digit overwrites the slot under the
   * caret instead of being appended and re-masked. onChange is left to bulk edits only (paste / autofill).
   */
  const handleKeyDown = (event) => {
    if (disabled || event.ctrlKey || event.metaKey || event.altKey) return;

    const input = event.currentTarget;
    const slots = readDigitSlots(inputValue);
    const selectionStart = input.selectionStart ?? 0;
    const selectionEnd = input.selectionEnd ?? selectionStart;
    const hasSelection = selectionEnd > selectionStart;

    if (isDigit(event.key)) {
      event.preventDefault();

      const writeIndex = digitIndexAtOrAfter(selectionStart);

      if (writeIndex >= MASK_LENGTH) return;

      const baseSlots = hasSelection ? clearSlotsInRange(slots, selectionStart, selectionEnd) : [...slots];
      baseSlots[slotOfDigitIndex(writeIndex)] = event.key;

      const caretIndex = digitIndexAtOrAfter(writeIndex + 1);
      commitSlots(baseSlots, { start: caretIndex, end: caretIndex });

      return;
    }

    if (event.key === "Backspace") {
      event.preventDefault();

      if (hasSelection) {
        const caretIndex = digitIndexAtOrAfter(selectionStart);
        commitSlots(clearSlotsInRange(slots, selectionStart, selectionEnd), { start: caretIndex, end: caretIndex });

        return;
      }

      const clearIndex = digitIndexBefore(selectionStart);
      const nextSlots = [...slots];
      nextSlots[slotOfDigitIndex(clearIndex)] = null;
      commitSlots(nextSlots, { start: clearIndex, end: clearIndex });

      return;
    }

    if (event.key === "Delete") {
      event.preventDefault();

      if (hasSelection) {
        const caretIndex = digitIndexAtOrAfter(selectionStart);
        commitSlots(clearSlotsInRange(slots, selectionStart, selectionEnd), { start: caretIndex, end: caretIndex });

        return;
      }

      const clearIndex = digitIndexAtOrAfter(selectionStart);

      if (clearIndex >= MASK_LENGTH) return;

      const nextSlots = [...slots];
      nextSlots[slotOfDigitIndex(clearIndex)] = null;
      commitSlots(nextSlots, { start: clearIndex, end: clearIndex });

      return;
    }

    if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
      if (isEmptyMask(slots)) return;

      event.preventDefault();

      const caretIndex =
        event.key === "ArrowLeft"
          ? digitIndexBefore(selectionStart)
          : Math.min(digitIndexAtOrAfter(selectionEnd + 1), DIGIT_INDEXES[DIGIT_INDEXES.length - 1]);
      input.setSelectionRange(caretIndex, caretIndex);

      return;
    }

    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();

      const segment = segmentAtIndex(Math.min(selectionStart, MASK_LENGTH - 1));
      const bounds = getSegmentBounds(segment);
      const segmentSlots = slots.slice(segment.slot, segment.slot + segment.size);
      const step = event.key === "ArrowUp" ? 1 : -1;
      const currentNumber = segmentSlots.every((slot) => slot !== null)
        ? parseInt(segmentSlots.join(""), 10)
        : bounds.fallback - step;

      let nextNumber = currentNumber + step;

      if (nextNumber > bounds.max) nextNumber = bounds.min;
      if (nextNumber < bounds.min) nextNumber = bounds.max;

      const nextSlots = [...slots];
      String(nextNumber)
        .padStart(segment.size, "0")
        .split("")
        .forEach((character, offset) => {
          nextSlots[segment.slot + offset] = character;
        });

      commitSlots(nextSlots, { start: segment.start, end: segment.end });
    }
  };

  /** Bulk edits only — paste, autofill and programmatic value setting. Single keystrokes go through handleKeyDown. */
  const handleInputChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, DIGIT_INDEXES.length);

    if (!digitsOnly) {
      commitSlots(readDigitSlots(""), { start: 0, end: 0 });

      return;
    }

    const nextSlots = DIGIT_INDEXES.map((_index, slot) => digitsOnly[slot] ?? null);
    const caretIndex =
      digitsOnly.length >= DIGIT_INDEXES.length ? MASK_LENGTH : digitIndexAtOrAfter(DIGIT_INDEXES[digitsOnly.length]);
    commitSlots(nextSlots, { start: caretIndex, end: caretIndex });
  };

  /** Clicking a segment selects it, so the next two keystrokes replace that segment rather than appending. */
  const selectSegmentAtCaret = () => {
    const input = inputRef.current;

    if (!input || isEmptyMask(readDigitSlots(input.value))) return;

    const segment = segmentAtIndex(Math.min(input.selectionStart ?? 0, MASK_LENGTH - 1));
    input.setSelectionRange(segment.start, segment.end);
  };

  const handleInputFocus = () => {
    setIsManualEntry(true);
    isEditingRef.current = true;
    setShowFormatHint(true);
    setFormatHintText("Enter date as DD-MM-YYYY");
  };

  const handleInputBlur = () => {
    setIsManualEntry(false);
    isEditingRef.current = false;

    const slots = readDigitSlots(inputValue);

    if (isEmptyMask(slots)) {
      setInputValue("");
      setShowFormatHint(false);
      setFormatHintText("");

      if (selectedDate) {
        setSelectedDate(null);
        onChange(null);
      }

      return;
    }

    const parsedDate = isCompleteMask(slots) ? parseManualDate(writeDigitSlots(slots)) : null;

    if (parsedDate && !isDateDisabled(parsedDate)) {
      setSelectedDate(parsedDate);
      setCurrentMonth(parsedDate);
      setInputValue(formatDate(parsedDate));
      setShowFormatHint(false);
      setFormatHintText("");
      onChange(parsedDate);

      return;
    }

    /*
     * The edit is being discarded, so it has to say why. Correcting a single segment can
     * easily produce a date the field forbids — changing the day of today's date on the
     * vaccine form makes it a future date, which that form does not allow — and the
     * value simply snapping back reads as the input losing the edit.
     */
    const completeButRefused = isCompleteMask(slots) && parsedDate;

    setFormatHintText(
      completeButRefused
        ? describeDisabledDate(parsedDate)
        : "Enter the date as DD-MM-YYYY"
    );
    setShowFormatHint(true);

    /** Falls back to the last committed date, as before. */
    if (selectedDate && !isNaN(selectedDate.getTime())) {
      setInputValue(formatDate(selectedDate));

      return;
    }

    setInputValue("");
    onChange(null);
  };

  const calculateDropdownPosition = () => {
    if (!containerRef.current) return {};

    const rect = containerRef.current.getBoundingClientRect();
    const vh = window.innerHeight;
    const vw = window.innerWidth;
    const gap = 8;
    const margin = 8;
    /** Real popup is taller than old 300px estimate (header + grid + padding) */
    const estimatedHeight = 420;
    const minScrollHeight = 240;

    /** Fixed calendar width; clamp on very narrow viewports */
    const calendarPopupWidth = 340;
    const dropdownWidth = Math.min(calendarPopupWidth, vw - 2 * margin);
    let left = rect.left;
    if (left + dropdownWidth > vw - margin) {
      left = vw - dropdownWidth - margin;
    }
    if (left < margin) {
      left = margin;
    }

    const spaceBelow = vh - rect.bottom - margin;
    const spaceAbove = rect.top - margin;

    let top;
    let maxHeight;

    const openBelow = () => {
      top = rect.bottom + gap;
      if (top + estimatedHeight > vh - margin) {
        maxHeight = Math.max(minScrollHeight, vh - top - margin);
      }
    };

    const openAbove = () => {
      top = rect.top - estimatedHeight - gap;
      if (top < margin) {
        maxHeight = Math.max(minScrollHeight, rect.top - gap - margin);
        top = margin;
      }
    };

    if (spaceBelow >= estimatedHeight) {
      openBelow();
    } else if (spaceAbove >= estimatedHeight) {
      openAbove();
    } else if (spaceBelow >= spaceAbove) {
      openBelow();
      if (top + estimatedHeight > vh - margin) {
        maxHeight = Math.max(minScrollHeight, vh - top - margin);
      }
    } else {
      openAbove();
    }

    const style = {
      position: "fixed",
      left: `${left}px`,
      top: `${top}px`,
      width: `${dropdownWidth}px`,
      bottom: "auto",
      ...(maxHeight != null
        ? {
            maxHeight: `${maxHeight}px`,
            overflowY: "auto",
            overflowX: "visible",
          }
        : {}),
    };

    return style;
  };

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className={s.emptyDay}></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const isSelected =
        selectedDate && date.toDateString() === selectedDate.toDateString();
      const isToday = date.toDateString() === new Date().toDateString();
      const isDisabled = isDateDisabled(date);

      days.push(
        <div
          key={day}
          className={`${s.calendarDay} ${isSelected ? s.selected : ""} ${
            isToday ? s.today : ""
          } ${isDisabled ? s.disabled : ""}`}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  useEffect(() => {
    if (isOpen) {
      setDropdownStyle(calculateDropdownPosition());
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleRepositioning = () => {
      setDropdownStyle(calculateDropdownPosition());
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    window.addEventListener("scroll", handleRepositioning, true);
    window.addEventListener("resize", handleRepositioning);
    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      window.removeEventListener("scroll", handleRepositioning, true);
      window.removeEventListener("resize", handleRepositioning);
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isOpen]);

  return (
    <div
      className={s.calendarContainer}
      ref={containerRef}
      style={{ position: "relative" }}
    >
      <div className={`${s.calendarInput} ${disabled ? s.inputDisabled : ""} ${error ? s.inputError : ""}`} data-calendar-input>
        <input
          type="text"
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onClick={selectSegmentAtCaret}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder || "Select"}
          disabled={disabled}
          className={s.dateInput}
        />
        <div
          className={s.calendarIcon}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <Calendar size={20} strokeWidth={1.75} />
        </div>
        {showFormatHint && formatHintText && (
          <div
            style={{
              position: "absolute",
              top: "100%",
              left: "0",
              right: "0",
              backgroundColor: "#f8f9fa",
              border: "1px solid #dee2e6",
              borderTop: "none",
              borderRadius: "0 0 4px 4px",
              padding: "6px 12px",
              fontSize: "12px",
              color: "#6c757d",
              zIndex: "10",
            }}
          >
            💡 {formatHintText}
          </div>
        )}
      </div>

      {isOpen && !disabled && ReactDOM.createPortal(
        <>
          <div className={s.calendarOverlay} data-calendar-popup onClick={() => setIsOpen(false)} />
          <div className={s.calendarDropdown} data-calendar-popup style={dropdownStyle}>
            <div className={s.calendarHeader}>
              <button
                className={s.navButton}
                onClick={() => navigateMonth(-1)}
                type="button"
              >
                <ChevronLeft size={16} />
              </button>
              <div className={s.monthYearSelectors}>
                <select
                  className={s.monthSelector}
                  value={currentMonth.getMonth()}
                  onChange={(e) => handleMonthChange(parseInt(e.target.value))}
                >
                  {months.map((month, index) => (
                    <option key={month} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                <select
                  className={s.yearSelector}
                  value={currentMonth.getFullYear()}
                  onChange={(e) => handleYearChange(parseInt(e.target.value))}
                >
                  {getYearRange().map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
              <button
                className={s.navButton}
                onClick={() => navigateMonth(1)}
                type="button"
              >
                <ChevronRight size={16} />
              </button>
            </div>

            <div className={s.calendarGrid}>
              <div className={s.weekdayHeaders}>
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                  (day) => (
                    <div key={day} className={s.weekdayHeader}>
                      {day}
                    </div>
                  )
                )}
              </div>
              <div className={s.daysGrid}>{renderCalendarDays()}</div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export { CustomCalendar };